import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import ActionMenu from '../../action-buttons/ActionButtons';
import Loader from '../../loader/Loader';
import PagedTable from '../../sorted-table/PagedTable';
import PayoutPaymentDetailsModal from './payoutPaymentDetailsModal';
import { getMarketplaceBreederPaymentTransactions } from '../../../api/MarketPlaceBreederPaymentTransactionApi';
import { centsToDollars } from '../../../services/account.service';
import { showToast } from '../../../services/toast.service';
import { MarketPlaceBreederPaymentTransaction } from '../../../types/interfaces/marketPlace.interfaces';
import { toastMessages } from '../../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, PAYMENT_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import './payoutPaymentHistory.scss';

const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString('en-US') : '-');
const formatAmount = (amountCents?: number) => `$${centsToDollars(amountCents ?? 0)}`;

const InfoLabel = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="payout-history-content">
    <label>{label}:</label>
    <label>{value}</label>
  </div>
);

const ViewMoreAction = ({
  history,
  onViewDetails,
}: {
  history: MarketPlaceBreederPaymentTransaction;
  onViewDetails: (history: MarketPlaceBreederPaymentTransaction) => void;
}) => (
  <ActionMenu
    actionButtons={[
      {
        name: BUTTON_CONSTANTS.VIEW_DETAILS,
        action: () => onViewDetails(history),
      },
    ]}
  />
);

const PayoutPaymentHistory: FC = () => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [payoutHistory, setPayoutHistory] = useState<MarketPlaceBreederPaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPayoutDetail, setSelectedPayoutDetail] = useState<MarketPlaceBreederPaymentTransaction | null>(null);

  useEffect(() => {
    const getPayoutHistory = async () => {
      if (!accountId) {
        setPayoutHistory([]);
        return;
      }

      try {
        setIsLoading(true);
        const { data } = await getMarketplaceBreederPaymentTransactions({
          filter: `accountId eq ${accountId}`,
          sort: '^billedDate',
        });
        setPayoutHistory(Array.isArray(data) ? data : []);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    void getPayoutHistory();
  }, [accountId]);

  return (
    <>
      <PayoutPaymentDetailsModal
        isOpen={selectedPayoutDetail != null}
        onClose={() => setSelectedPayoutDetail(null)}
        payoutDetail={selectedPayoutDetail}
      />

      <div className="payout-payment-history">
        <div className="desk-payout-history">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.BILL_DATE, sortValue: 'billedDate' },
              { displayName: PAYMENT_CONSTANTS.TOTAL_AMOUNT },
              { displayName: TABLE_HEADER_CONSTANTS.REFERENCE_ID },
              { displayName: TABLE_HEADER_CONSTANTS.BILLING_CYCLE_START },
              { displayName: TABLE_HEADER_CONSTANTS.BILLING_CYCLE_END },
              { displayName: TABLE_HEADER_CONSTANTS.ACTION },
            ]}
            sortBy="^billedDate"
            filter={accountId ? `accountId eq ${accountId}` : undefined}
            getData={getMarketplaceBreederPaymentTransactions}
            buildRow={(history: MarketPlaceBreederPaymentTransaction) => [
              formatDate(history.billedDate),
              formatAmount(history.totalAmountCents),
              history.transactionReferenceId ?? '-',
              formatDate(history.billingCycleStartDate),
              formatDate(history.billingCycleEndDate),
              <ViewMoreAction history={history} onViewDetails={setSelectedPayoutDetail} />,
            ]}
            height={240}
            pageSize={6}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
          />
        </div>

        <div className="xs-payout-history">
          {!isLoading && payoutHistory.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            payoutHistory.map(history => (
              <div key={history.breederPaymentTransactionId} className="card payout-history-card">
                <div className="payout-history-info">
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.BILL_DATE} value={formatDate(history.billedDate)} />
                  <InfoLabel label={PAYMENT_CONSTANTS.TOTAL_AMOUNT} value={formatAmount(history.totalAmountCents)} />
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.REFERENCE_ID} value={history.transactionReferenceId ?? '-'} />
                </div>
                <div className="payout-history-info">
                  <InfoLabel
                    label={TABLE_HEADER_CONSTANTS.BILLING_CYCLE_START}
                    value={formatDate(history.billingCycleStartDate)}
                  />
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.BILLING_CYCLE_END} value={formatDate(history.billingCycleEndDate)} />
                  <InfoLabel
                    label={TABLE_HEADER_CONSTANTS.ACTION}
                    value={<ViewMoreAction history={history} onViewDetails={setSelectedPayoutDetail} />}
                  />
                </div>
              </div>
            ))
          )}
          {isLoading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>
    </>
  );
};

export default PayoutPaymentHistory;
